import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const TODAY = new Date().toISOString().split('T')[0];
const NOW_TIME = new Date().toTimeString().slice(0, 5);
const PURPOSES = ['Guest Pickup','Guest Drop','Employee Work','Factory Visit','Internal Courier','Airport Transfer','Medical','Maintenance','Other'];

const STATUS_CONFIG = {
  planned:   { label: 'Planned',   color: 'bg-amber-100 text-amber-700 border-amber-200',    dot: 'bg-amber-400',  icon: '🟡' },
  started:   { label: 'On Trip',   color: 'bg-blue-100 text-blue-700 border-blue-200',       dot: 'bg-blue-500',   icon: '🔵' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200',    dot: 'bg-green-500',  icon: '🟢' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600 border-red-200',          dot: 'bg-red-400',    icon: '🔴' },
};

const EMPTY_NEW = {
  trip_date: TODAY, vehicle_id: '', driver_id: '', purpose: 'Guest Pickup',
  guest_name: '', employee_id: '', department: '', from_location: '', to_location: '',
  expected_return_date: '', remarks: ''
};

const EMPTY_START = { km_start: '', start_time: NOW_TIME, trip_date: TODAY };

const EMPTY_COMPLETE = {
  return_date: TODAY, end_time: NOW_TIME, km_end: '',
  fuel_cost: '', toll_expense: '', parking_charges: '', driver_allowance: '', other_expense: '',
  remarks: ''
};

function fmt(n) { return Number(n || 0).toLocaleString(); }
function fmtDate(d) {
  if (!d) return '—';
  const s = String(d).slice(0, 10); // handles both "2026-05-19" and "2026-05-19T00:00:00.000Z"
  return new Date(s + 'T00:00:00').toLocaleDateString('en-PK', { day:'2-digit', month:'short', year:'numeric' });
}
function fmtTs(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-PK', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
}

export default function VehicleTrips() {
  const { user } = useAuth();
  const [trips, setTrips]     = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('active'); // 'active' | 'history'
  const [histFrom, setHistFrom] = useState('');
  const [histTo, setHistTo]     = useState('');

  // Modals
  const [showNew,      setShowNew]      = useState(false);
  const [newForm,      setNewForm]      = useState(EMPTY_NEW);
  const [showStart,    setShowStart]    = useState(false);
  const [startTrip,    setStartTrip]    = useState(null);
  const [startForm,    setStartForm]    = useState(EMPTY_START);
  const [showComplete, setShowComplete] = useState(false);
  const [compTrip,     setCompTrip]     = useState(null);
  const [compForm,     setCompForm]     = useState(EMPTY_COMPLETE);
  const [showDetail,   setShowDetail]   = useState(false);
  const [detailTrip,   setDetailTrip]   = useState(null);
  const [showEdit,     setShowEdit]     = useState(false);
  const [editTrip,     setEditTrip]     = useState(null);
  const [editForm,     setEditForm]     = useState({});
  const [submitting,   setSubmitting]   = useState(false);

  // ---- Load ----
  const load = async () => {
    if (!user) return;
    setLoading(true);
    const params = tab === 'active'
      ? '?active_only=true'
      : `?${histFrom ? `from=${histFrom}` : ''}${histTo ? `&to=${histTo}` : ''}${(!histFrom && !histTo) ? `from=${TODAY}&to=${TODAY}` : ''}`;
    try { const r = await api.get(`/api/trips${params}`); setTrips(r.data); }
    catch { setTrips([]); }
    try { const r = await api.get('/api/vehicles'); setVehicles(r.data); }
    catch { setVehicles([]); }
    try { const r = await api.get('/api/drivers'); setDrivers(r.data); }
    catch { setDrivers([]); }
    try { const r = await api.get('/api/employees/active'); setEmployees(r.data); }
    catch { setEmployees([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role, tab, histFrom, histTo]);

  // When new trip vehicle is selected, pre-fill km_start in start modal
  const selectedVehicle = vehicles.find(v => v.id === Number(newForm.vehicle_id));

  // ---- Create Trip ----
  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/api/trips', {
        ...newForm,
        vehicle_id: Number(newForm.vehicle_id),
        driver_id: Number(newForm.driver_id),
        employee_id: newForm.employee_id ? Number(newForm.employee_id) : null,
      });
      setShowNew(false); setNewForm(EMPTY_NEW);
      setTab('active'); load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to create trip'); }
    finally { setSubmitting(false); }
  };

  // ---- Start Trip ----
  const openStart = (trip) => {
    const veh = vehicles.find(v => v.id === Number(trip.vehicle_id));
    setStartTrip(trip);
    setStartForm({ km_start: veh?.current_km || trip.km_start || '', start_time: NOW_TIME, trip_date: TODAY });
    setShowStart(true);
  };
  const handleStart = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/api/trips/${startTrip.id}/start`, {
        km_start: Number(startForm.km_start),
        start_time: startForm.start_time,
        trip_date: startForm.trip_date,
      });
      setShowStart(false); setStartTrip(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to start trip'); }
    finally { setSubmitting(false); }
  };

  // ---- Complete Trip ----
  const openComplete = (trip) => {
    const veh = vehicles.find(v => v.id === Number(trip.vehicle_id));
    setCompTrip(trip);
    setCompForm({
      return_date: TODAY, end_time: NOW_TIME,
      km_end: veh?.current_km || '',
      fuel_cost: '', toll_expense: '', parking_charges: '',
      driver_allowance: '', other_expense: '', remarks: ''
    });
    setShowComplete(true);
  };
  const handleComplete = async (e) => {
    e.preventDefault();
    if (!compForm.km_end) { alert('Closing KM is required'); return; }
    if (Number(compForm.km_end) < Number(compTrip.km_start || 0)) {
      alert('Closing KM cannot be less than Opening KM'); return;
    }
    setSubmitting(true);
    try {
      await api.put(`/api/trips/${compTrip.id}/complete`, {
        km_end:           Number(compForm.km_end),
        end_time:         compForm.end_time,
        return_date:      compForm.return_date,
        fuel_cost:        Number(compForm.fuel_cost)        || 0,
        toll_expense:     Number(compForm.toll_expense)     || 0,
        parking_charges:  Number(compForm.parking_charges)  || 0,
        driver_allowance: Number(compForm.driver_allowance) || 0,
        other_expense:    Number(compForm.other_expense)    || 0,
        remarks:          compForm.remarks,
      });
      setShowComplete(false); setCompTrip(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to complete trip'); }
    finally { setSubmitting(false); }
  };

  // ---- Cancel Trip ----
  const handleCancel = async (trip) => {
    if (!window.confirm(`Cancel trip for ${trip.vehicle_number || 'vehicle'}?\nThis will release the vehicle and driver.`)) return;
    try {
      await api.put(`/api/trips/${trip.id}/cancel`);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to cancel'); }
  };

  // ---- Edit Planned Trip ----
  const openEdit = (trip) => {
    setEditTrip(trip);
    setEditForm({
      trip_date: trip.trip_date || TODAY,
      purpose: trip.purpose || '',
      guest_name: trip.guest_name || '',
      employee_id: trip.employee_id || '',
      department: trip.department || '',
      from_location: trip.from_location || '',
      to_location: trip.to_location || '',
      expected_return_date: trip.expected_return_date || '',
      km_start: trip.km_start || 0,
      remarks: trip.remarks || '',
    });
    setShowEdit(true);
  };
  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put(`/api/trips/${editTrip.id}`, {
        ...editForm,
        employee_id: editForm.employee_id ? Number(editForm.employee_id) : null,
      });
      setShowEdit(false); setEditTrip(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update trip'); }
    finally { setSubmitting(false); }
  };

  // ---- Computed totals for complete form ----
  const totalKm = compTrip ? Math.max(0, Number(compForm.km_end || 0) - Number(compTrip.km_start || 0)) : 0;
  const totalExp = ['fuel_cost','toll_expense','parking_charges','driver_allowance','other_expense']
    .reduce((s, k) => s + (Number(compForm[k]) || 0), 0);

  const activeTrips = trips.filter(t => ['planned','started'].includes(t.status));
  const histTrips   = trips.filter(t => ['completed','cancelled'].includes(t.status));

  // ================================================================
  // RENDER
  // ================================================================
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Vehicle Trips</h1>
          <p className="text-gray-500 text-sm">
            {activeTrips.length} active · {tab === 'history' ? `${histTrips.length} in history` : ''}
          </p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          ➕ New Trip
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[['active','🚗 Active Trips'],['history','📋 History']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === key ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* History date filter */}
      {tab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
          <span className="text-sm text-gray-500 font-medium">Date range:</span>
          <input type="date" value={histFrom} onChange={e => setHistFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <span className="text-gray-400">to</span>
          <input type="date" value={histTo} onChange={e => setHistTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          {(histFrom || histTo) && (
            <button onClick={() => { setHistFrom(''); setHistTo(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline">Clear</button>
          )}
        </div>
      )}

      {/* Trip Cards */}
      {loading ? (
        <div className="p-12 text-center text-gray-400">
          <div className="text-4xl animate-pulse mb-2">🚗</div>
          <p>Loading trips...</p>
        </div>
      ) : tab === 'active' ? (
        activeTrips.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">🚗</div>
            <p className="font-medium">No active trips</p>
            <p className="text-sm mt-1">Click "New Trip" to plan a trip</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {activeTrips.map(t => <TripCard key={t.id} trip={t}
              onStart={() => openStart(t)}
              onComplete={() => openComplete(t)}
              onCancel={() => handleCancel(t)}
              onEdit={() => openEdit(t)}
              onView={() => { setDetailTrip(t); setShowDetail(true); }}
            />)}
          </div>
        )
      ) : (
        histTrips.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400">
            <p className="text-4xl mb-2">📋</p>
            <p>No trips in this date range</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date','Vehicle','Driver','Route','Purpose','KM','Total Exp','Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {histTrips.map(t => {
                  const cfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.planned;
                  const totalE = (Number(t.fuel_cost||0) + Number(t.toll_expense||0) + Number(t.parking_charges||0)
                                + Number(t.driver_allowance||0) + Number(t.other_expense||0));
                  const totalK = t.km_end && t.km_start ? Number(t.km_end) - Number(t.km_start) : null;
                  return (
                    <tr key={t.id} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => { setDetailTrip(t); setShowDetail(true); }}>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(t.trip_date)}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{t.vehicle_number || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{t.driver_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        <div>{t.from_location || '—'}</div>
                        <div>→ {t.to_location || '—'}</div>
                      </td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{t.purpose || '—'}</span></td>
                      <td className="px-4 py-3 text-gray-600">{totalK !== null ? `${totalK} km` : '—'}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">Rs {fmt(totalE)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>{cfg.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ===================== MODALS ===================== */}

      {/* NEW TRIP MODAL */}
      {showNew && (
        <Modal title="New Trip" onClose={() => { setShowNew(false); setNewForm(EMPTY_NEW); }}>
          <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Trip Date *</label>
              <input type="date" value={newForm.trip_date} onChange={e => setNewForm({...newForm,trip_date:e.target.value})} required className="input" />
            </div>
            <div>
              <label className="label">Expected Return Date</label>
              <input type="date" value={newForm.expected_return_date} onChange={e => setNewForm({...newForm,expected_return_date:e.target.value})} className="input" />
            </div>
            <div>
              <label className="label">Vehicle *</label>
              <select value={newForm.vehicle_id} onChange={e => setNewForm({...newForm,vehicle_id:e.target.value})} required className="input">
                <option value="">— Select Vehicle —</option>
                {vehicles.filter(v => v.status === 'available').map(v => (
                  <option key={v.id} value={v.id}>{v.vehicle_number} ({v.vehicle_type}) — {v.current_km||0} km</option>
                ))}
              </select>
              {vehicles.filter(v => v.status === 'on_trip').length > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {vehicles.filter(v => v.status === 'on_trip').length} vehicle(s) currently on trip (hidden)
                </p>
              )}
            </div>
            <div>
              <label className="label">Driver *</label>
              <select value={newForm.driver_id} onChange={e => setNewForm({...newForm,driver_id:e.target.value})} required className="input">
                <option value="">— Select Driver —</option>
                {drivers.filter(d => d.status === 'available').map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              {drivers.filter(d => d.status === 'on_trip').length > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {drivers.filter(d => d.status === 'on_trip').length} driver(s) currently on trip (hidden)
                </p>
              )}
            </div>
            <div>
              <label className="label">Purpose</label>
              <select value={newForm.purpose} onChange={e => setNewForm({...newForm,purpose:e.target.value})} className="input">
                {PURPOSES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Guest Name</label>
              <input type="text" value={newForm.guest_name} onChange={e => setNewForm({...newForm,guest_name:e.target.value})} placeholder="If picking up a guest" className="input" />
            </div>
            <div>
              <label className="label">Employee (Optional)</label>
              <select value={newForm.employee_id} onChange={e => setNewForm({...newForm,employee_id:e.target.value})} className="input">
                <option value="">— None —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Department</label>
              <input type="text" value={newForm.department} onChange={e => setNewForm({...newForm,department:e.target.value})} className="input" />
            </div>
            <div>
              <label className="label">From Location</label>
              <input type="text" value={newForm.from_location} onChange={e => setNewForm({...newForm,from_location:e.target.value})} placeholder="Starting point" className="input" />
            </div>
            <div>
              <label className="label">To / Destination</label>
              <input type="text" value={newForm.to_location} onChange={e => setNewForm({...newForm,to_location:e.target.value})} placeholder="Destination" className="input" />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Notes / Remarks</label>
              <textarea value={newForm.remarks} onChange={e => setNewForm({...newForm,remarks:e.target.value})} rows={2} className="input" />
            </div>
            <div className="sm:col-span-2">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                ℹ️ Trip will be created as <strong>Planned</strong>. Vehicle and driver will be marked as <strong>On Trip</strong> immediately.
                When the vehicle actually leaves, click <strong>Start Trip</strong> to record the departure time and opening KM.
              </div>
            </div>
            <div className="sm:col-span-2 flex gap-3 pt-2 border-t">
              <button type="submit" disabled={submitting}
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50">
                {submitting ? 'Creating...' : '✅ Create Trip'}
              </button>
              <button type="button" onClick={() => { setShowNew(false); setNewForm(EMPTY_NEW); }}
                className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* START TRIP MODAL */}
      {showStart && startTrip && (
        <Modal title={`Start Trip — ${startTrip.vehicle_number || ''}`} onClose={() => setShowStart(false)} size="sm">
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <div>🚗 <strong>{startTrip.vehicle_number}</strong> &nbsp;👤 <strong>{startTrip.driver_name}</strong></div>
            <div className="mt-1 text-xs text-amber-700">
              {startTrip.from_location || '—'} → {startTrip.to_location || '—'}
            </div>
          </div>
          <form onSubmit={handleStart} className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Departure Date *</label>
              <input type="date" value={startForm.trip_date} onChange={e => setStartForm({...startForm,trip_date:e.target.value})} required className="input" />
            </div>
            <div>
              <label className="label">Departure Time *</label>
              <input type="time" value={startForm.start_time} onChange={e => setStartForm({...startForm,start_time:e.target.value})} required className="input" />
            </div>
            <div className="col-span-2">
              <label className="label">Opening KM (Odometer Reading) *</label>
              <input type="number" value={startForm.km_start} onChange={e => setStartForm({...startForm,km_start:e.target.value})}
                required min="0" placeholder="Reading from vehicle odometer" className="input" />
            </div>
            <div className="col-span-2 flex gap-3 pt-2 border-t">
              <button type="submit" disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50">
                {submitting ? 'Starting...' : '▶ Start Trip'}
              </button>
              <button type="button" onClick={() => setShowStart(false)}
                className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* COMPLETE TRIP MODAL */}
      {showComplete && compTrip && (
        <Modal title={`Complete Trip — ${compTrip.vehicle_number || ''}`} onClose={() => setShowComplete(false)} size="lg">
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
            <div>🚗 <strong>{compTrip.vehicle_number}</strong> &nbsp;👤 <strong>{compTrip.driver_name}</strong></div>
            <div className="mt-1 text-xs text-green-700">
              {compTrip.from_location || '—'} → {compTrip.to_location || '—'} &nbsp;|&nbsp;
              Opening KM: <strong>{compTrip.km_start || 0}</strong>
            </div>
          </div>
          <form onSubmit={handleComplete} className="grid grid-cols-2 gap-4">
            {/* Return info */}
            <div>
              <label className="label">Return Date *</label>
              <input type="date" value={compForm.return_date} onChange={e => setCompForm({...compForm,return_date:e.target.value})} required className="input" />
            </div>
            <div>
              <label className="label">Return Time</label>
              <input type="time" value={compForm.end_time} onChange={e => setCompForm({...compForm,end_time:e.target.value})} className="input" />
            </div>
            <div className="col-span-2">
              <label className="label">Closing KM (Odometer Reading) *</label>
              <input type="number" value={compForm.km_end} onChange={e => setCompForm({...compForm,km_end:e.target.value})}
                required min={compTrip.km_start || 0} placeholder="Current odometer reading" className="input" />
              {compForm.km_end && <p className="text-xs text-blue-600 mt-1">
                Total KM: <strong>{totalKm} km</strong> (Closing {compForm.km_end} − Opening {compTrip.km_start || 0})
              </p>}
            </div>
            {/* Expense section */}
            <div className="col-span-2 border-t pt-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Trip Expenses (Rs)</p>
            </div>
            <div>
              <label className="label">⛽ Fuel Cost</label>
              <input type="number" min="0" value={compForm.fuel_cost} onChange={e => setCompForm({...compForm,fuel_cost:e.target.value})} placeholder="0" className="input" />
            </div>
            <div>
              <label className="label">🛣️ Toll Charges</label>
              <input type="number" min="0" value={compForm.toll_expense} onChange={e => setCompForm({...compForm,toll_expense:e.target.value})} placeholder="0" className="input" />
            </div>
            <div>
              <label className="label">🅿️ Parking Charges</label>
              <input type="number" min="0" value={compForm.parking_charges} onChange={e => setCompForm({...compForm,parking_charges:e.target.value})} placeholder="0" className="input" />
            </div>
            <div>
              <label className="label">👤 Driver Allowance</label>
              <input type="number" min="0" value={compForm.driver_allowance} onChange={e => setCompForm({...compForm,driver_allowance:e.target.value})} placeholder="0" className="input" />
            </div>
            <div>
              <label className="label">📦 Other Expenses</label>
              <input type="number" min="0" value={compForm.other_expense} onChange={e => setCompForm({...compForm,other_expense:e.target.value})} placeholder="0" className="input" />
            </div>
            <div>
              <label className="label">Total Expense</label>
              <div className="w-full border border-blue-200 bg-blue-50 rounded-lg px-3 py-2 text-sm font-bold text-blue-700">
                Rs {fmt(totalExp)}
              </div>
            </div>
            <div className="col-span-2">
              <label className="label">Remarks</label>
              <textarea value={compForm.remarks} onChange={e => setCompForm({...compForm,remarks:e.target.value})} rows={2} className="input" placeholder="Optional notes about the trip" />
            </div>
            <div className="col-span-2 bg-green-50 border border-green-100 rounded-lg p-3 text-xs text-green-700">
              ✅ On completion:&nbsp;
              <strong>Vehicle</strong> → Available &nbsp;|&nbsp;
              <strong>Driver</strong> → Available &nbsp;|&nbsp;
              {totalExp > 0 && <><strong>Expenses</strong> (Rs {fmt(totalExp)}) → Auto-saved to Expenses module</>}
            </div>
            <div className="col-span-2 flex gap-3 pt-2 border-t">
              <button type="submit" disabled={submitting}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50">
                {submitting ? 'Completing...' : '✅ Complete Trip'}
              </button>
              <button type="button" onClick={() => setShowComplete(false)}
                className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* EDIT PLANNED TRIP MODAL */}
      {showEdit && editTrip && (
        <Modal title="Edit Planned Trip" onClose={() => setShowEdit(false)}>
          <form onSubmit={handleEdit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Trip Date</label>
              <input type="date" value={editForm.trip_date} onChange={e => setEditForm({...editForm,trip_date:e.target.value})} className="input" />
            </div>
            <div>
              <label className="label">Expected Return</label>
              <input type="date" value={editForm.expected_return_date} onChange={e => setEditForm({...editForm,expected_return_date:e.target.value})} className="input" />
            </div>
            <div>
              <label className="label">Purpose</label>
              <select value={editForm.purpose} onChange={e => setEditForm({...editForm,purpose:e.target.value})} className="input">
                {PURPOSES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Opening KM</label>
              <input type="number" min="0" value={editForm.km_start} onChange={e => setEditForm({...editForm,km_start:e.target.value})} className="input" />
            </div>
            <div>
              <label className="label">From Location</label>
              <input type="text" value={editForm.from_location} onChange={e => setEditForm({...editForm,from_location:e.target.value})} className="input" />
            </div>
            <div>
              <label className="label">To / Destination</label>
              <input type="text" value={editForm.to_location} onChange={e => setEditForm({...editForm,to_location:e.target.value})} className="input" />
            </div>
            <div>
              <label className="label">Guest Name</label>
              <input type="text" value={editForm.guest_name} onChange={e => setEditForm({...editForm,guest_name:e.target.value})} className="input" />
            </div>
            <div>
              <label className="label">Department</label>
              <input type="text" value={editForm.department} onChange={e => setEditForm({...editForm,department:e.target.value})} className="input" />
            </div>
            <div className="col-span-2">
              <label className="label">Remarks</label>
              <textarea value={editForm.remarks} onChange={e => setEditForm({...editForm,remarks:e.target.value})} rows={2} className="input" />
            </div>
            <div className="col-span-2 flex gap-3 pt-2 border-t">
              <button type="submit" disabled={submitting}
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={() => setShowEdit(false)}
                className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* TRIP DETAIL MODAL */}
      {showDetail && detailTrip && (
        <TripDetailModal trip={detailTrip} onClose={() => setShowDetail(false)} />
      )}
    </div>
  );
}

// ================================================================
// TRIP CARD (Active trips)
// ================================================================
function TripCard({ trip, onStart, onComplete, onCancel, onEdit, onView }) {
  const cfg = STATUS_CONFIG[trip.status] || STATUS_CONFIG.planned;
  const totalE = Number(trip.fuel_cost||0) + Number(trip.toll_expense||0) + Number(trip.parking_charges||0)
               + Number(trip.driver_allowance||0) + Number(trip.other_expense||0);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      {/* Card Header */}
      <div className="p-4 border-b border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-xl">🚗</div>
          <div>
            <p className="font-bold text-gray-800">{trip.vehicle_number || '—'}</p>
            <p className="text-xs text-gray-500">👤 {trip.driver_name || '—'}</p>
          </div>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
          {cfg.icon} {cfg.label}
        </span>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-2.5">
        {/* Route */}
        <div className="flex items-start gap-2">
          <span className="text-gray-400 text-sm mt-0.5">📍</span>
          <div className="text-sm">
            <div className="font-medium text-gray-700">{trip.from_location || 'Starting point not set'}</div>
            <div className="text-gray-400 text-xs">↓</div>
            <div className="font-medium text-gray-700">{trip.to_location || 'Destination not set'}</div>
          </div>
        </div>

        {/* Info Row */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          {trip.purpose && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{trip.purpose}</span>}
          {trip.trip_date && <span>📅 {new Date(String(trip.trip_date).slice(0,10) + 'T00:00:00').toLocaleDateString('en-PK', {day:'2-digit',month:'short'})}</span>}
          {trip.expected_return_date && <span>🔄 Return: {new Date(String(trip.expected_return_date).slice(0,10) + 'T00:00:00').toLocaleDateString('en-PK', {day:'2-digit',month:'short'})}</span>}
          {trip.guest_name && <span>👥 {trip.guest_name}</span>}
          {trip.employee_name && <span>👔 {trip.employee_name}</span>}
        </div>

        {/* KM & Start info for started trips */}
        {trip.status === 'started' && (
          <div className="flex gap-4 text-xs bg-blue-50 rounded-lg p-2.5">
            <span>🛣️ Opening KM: <strong>{trip.km_start || 0}</strong></span>
            {trip.start_time && <span>⏱ Departed: <strong>{trip.start_time}</strong></span>}
            {trip.started_at && <span className="text-blue-600">Started: {new Date(trip.started_at).toLocaleDateString('en-PK',{day:'2-digit',month:'short'})}</span>}
          </div>
        )}

        {/* Timeline */}
        <TripTimeline status={trip.status} />
      </div>

      {/* Card Actions */}
      <div className="p-4 border-t border-gray-50 flex flex-wrap gap-2">
        {trip.status === 'planned' && <>
          <button onClick={onStart}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-semibold">
            ▶ Start Trip
          </button>
          <button onClick={onEdit}
            className="px-3 py-2 border border-gray-200 rounded-lg text-xs hover:bg-gray-50">✏️ Edit</button>
          <button onClick={onCancel}
            className="px-3 py-2 border border-red-200 text-red-600 rounded-lg text-xs hover:bg-red-50">✕ Cancel</button>
        </>}
        {trip.status === 'started' && <>
          <button onClick={onComplete}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-xs font-semibold">
            ✅ Complete Trip
          </button>
          <button onClick={onCancel}
            className="px-3 py-2 border border-red-200 text-red-600 rounded-lg text-xs hover:bg-red-50">✕ Cancel</button>
        </>}
        <button onClick={onView}
          className="px-3 py-2 border border-gray-200 rounded-lg text-xs hover:bg-gray-50">👁 Details</button>
      </div>
    </div>
  );
}

// ================================================================
// TRIP TIMELINE (visual progress indicator)
// ================================================================
function TripTimeline({ status }) {
  const steps = [
    { key:'planned',   label:'Planned' },
    { key:'started',   label:'Started' },
    { key:'completed', label:'Completed' },
  ];
  const order = { planned:0, started:1, completed:2, cancelled:2 };
  const current = order[status] ?? 0;
  const isCancelled = status === 'cancelled';

  return (
    <div className="flex items-center gap-0 mt-1">
      {steps.map((s, i) => {
        const done  = !isCancelled && current > i;
        const active = !isCancelled && current === i;
        const canc  = isCancelled && i === current;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                canc   ? 'bg-red-100 text-red-500 border-2 border-red-300' :
                done   ? 'bg-green-500 text-white' :
                active ? 'bg-blue-600 text-white' :
                         'bg-gray-100 text-gray-400 border-2 border-gray-200'
              }`}>
                {canc ? '✕' : done ? '✓' : i + 1}
              </div>
              <span className={`text-xs mt-0.5 ${active ? 'text-blue-600 font-semibold' : done ? 'text-green-600' : 'text-gray-400'}`}>
                {isCancelled && i === current ? 'Cancelled' : s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mb-3 ${done ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ================================================================
// TRIP DETAIL MODAL
// ================================================================
function TripDetailModal({ trip, onClose }) {
  const cfg = STATUS_CONFIG[trip.status] || STATUS_CONFIG.planned;
  const totalKm = trip.km_end && trip.km_start ? Number(trip.km_end) - Number(trip.km_start) : null;
  const expenses = {
    'Fuel':             Number(trip.fuel_cost        || 0),
    'Toll':             Number(trip.toll_expense     || 0),
    'Parking':          Number(trip.parking_charges  || 0),
    'Driver Allowance': Number(trip.driver_allowance || 0),
    'Other':            Number(trip.other_expense    || 0),
  };
  const totalExp = Object.values(expenses).reduce((s, v) => s + v, 0);

  return (
    <Modal title="Trip Details" onClose={onClose} size="lg">
      {/* Status badge */}
      <div className="flex items-center gap-3 mb-5">
        <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${cfg.color}`}>{cfg.icon} {cfg.label}</span>
        <TripTimeline status={trip.status} />
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        {/* Vehicle & Driver */}
        <InfoBox label="Vehicle" value={trip.vehicle_number} />
        <InfoBox label="Driver" value={trip.driver_name} />
        <InfoBox label="Trip Date" value={fmtDate(trip.trip_date)} />
        {trip.expected_return_date && <InfoBox label="Expected Return" value={fmtDate(trip.expected_return_date)} />}
        <InfoBox label="Purpose" value={trip.purpose} />
        {(trip.guest_name || trip.employee_name) && (
          <InfoBox label={trip.guest_name ? 'Guest' : 'Employee'} value={trip.guest_name || trip.employee_name} />
        )}
        <div className="col-span-2 grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-3">
          <InfoBox label="From" value={trip.from_location || '—'} />
          <InfoBox label="To" value={trip.to_location || '—'} />
        </div>

        {/* KM Section */}
        <div className="col-span-2 border-t pt-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Odometer</p>
          <div className="grid grid-cols-3 gap-3">
            <InfoBox label="Opening KM" value={trip.km_start ? `${Number(trip.km_start).toLocaleString()} km` : '—'} />
            <InfoBox label="Closing KM" value={trip.km_end   ? `${Number(trip.km_end  ).toLocaleString()} km` : '—'} />
            <InfoBox label="Total KM"   value={totalKm !== null ? `${totalKm} km` : '—'} highlight />
          </div>
        </div>

        {/* Timing */}
        {(trip.start_time || trip.end_time || trip.started_at || trip.completed_at) && (
          <div className="col-span-2 border-t pt-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Timeline</p>
            <div className="space-y-1 text-xs text-gray-600">
              {trip.started_at   && <div>▶ <strong>Started:</strong> {fmtTs(trip.started_at)} {trip.start_time && `at ${trip.start_time}`}</div>}
              {trip.completed_at && <div>✅ <strong>Completed:</strong> {fmtTs(trip.completed_at)} {trip.end_time && `at ${trip.end_time}`}</div>}
              {trip.cancelled_at && <div>✕ <strong>Cancelled:</strong> {fmtTs(trip.cancelled_at)}</div>}
            </div>
          </div>
        )}

        {/* Expenses */}
        {totalExp > 0 && (
          <div className="col-span-2 border-t pt-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Expense Breakdown</p>
            <div className="space-y-1">
              {Object.entries(expenses).filter(([,v]) => v > 0).map(([label, val]) => (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800">Rs {val.toLocaleString()}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold border-t pt-1 mt-1">
                <span className="text-gray-700">Total Expense</span>
                <span className="text-blue-700">Rs {totalExp.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {/* Remarks */}
        {trip.remarks && (
          <div className="col-span-2 border-t pt-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Remarks</p>
            <p className="text-sm text-gray-600">{trip.remarks}</p>
          </div>
        )}

        {/* Meta */}
        <div className="col-span-2 border-t pt-3 text-xs text-gray-400 flex justify-between">
          <span>Trip #{trip.id}</span>
          {trip.created_by_name && <span>Created by: {trip.created_by_name}</span>}
        </div>
      </div>
    </Modal>
  );
}

// ================================================================
// SHARED COMPONENTS
// ================================================================
function Modal({ title, onClose, children, size = 'md' }) {
  const maxW = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-xl';
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} max-h-[90vh] overflow-y-auto`}>
        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function InfoBox({ label, value, highlight }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`font-medium ${highlight ? 'text-blue-700 text-base' : 'text-gray-800'}`}>{value || '—'}</p>
    </div>
  );
}

// Tailwind utility class shorthands (injected via inline style trick for brevity)
// These are used as className="input" and className="label" above
// Inject global style at top of render to override
const style = document.createElement('style');
style.textContent = `
  .input { width:100%; border:1px solid #d1d5db; border-radius:0.5rem; padding:0.5rem 0.75rem; font-size:0.875rem; outline:none; }
  .input:focus { outline: 2px solid #3b82f6; outline-offset: 0; }
  .label { display:block; font-size:0.875rem; font-weight:500; color:#374151; margin-bottom:0.25rem; }
`;
if (!document.head.querySelector('style[data-vt]')) {
  style.setAttribute('data-vt','1');
  document.head.appendChild(style);
}
